import { format, fromNumber, type MonetaryValue } from 'ezmoney';
import Fuse from 'fuse.js';

import { type Company } from 'modules/app/hooks/useCompany';
import { type User } from 'modules/app/hooks/useUser';
import type { CardBlockingReason } from 'modules/physical-cards/card';
import {
  type I18nKey,
  type TGlobalFunctionTyped,
} from 'src/core/common/hooks/useTranslation';
import { type Language } from 'src/core/config/i18n';
import { type CurrenciesKey } from 'src/core/config/money';
import { hasTeamsFeatureEnabled } from 'src/core/utils/entities/user';
import { isGroupAdmin } from 'src/core/utils/entities/userTyped';

import { prependUrlScheme } from '../../payments/utils/prependUrlScheme';

export type Subscription = {
  is_renew_possible: boolean;
  was_transferred: boolean;
  blocking_reason?: CardBlockingReason;
  is_renew_required: boolean;
  months_before_card_expiry: number;
  renews_soon: boolean;
  id: string;
  organisation_id: string;
  interval?: 'MONTHLY' | 'YEARLY' | 'QUARTERLY' | null;
  realignment_date: string | null;
  company_id: string;
  request_id: string;
  supplier_id: string;
  user_id: string;
  group_id: string | null;
  supplier_billing_url: string | null;
  card_id: string;
  status: 'active' | 'inactive';
  description: string;
  currency_declared: Company['currency'];
  budget_declared: number;
  budget_real_estimated: number;
  budget_real_estimated_with_fees: number;
  invoice_recurrence: 'per_payment' | 'monthly';
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  nb_payments: number;
  outgoings_billed: number;
  outgoings_billed_without_fees: number;
  outgoings_declared: number;
  outgoing_fees: number;
  account: {
    id: string;
    currency: string;
    balance: number;
    version: 'v2';
  };
  card: {
    balance: number;
    available: number;
    pending: number;
    id: string;
    card_visible_number: string;
    card_exp_date: string | null;
    account_id: string;
    provider_user_id: string;
    brand: 'visa' | 'mastercard';
    payment_system: 'debit' | 'prepaid';
    automatic_reload_fail?: boolean | null;
    available_exchanged: number;
    banking_provider: 'bankable' | 'marqeta' | 'sfs';
  };
  increase_request: { id: string } | null;
  supplier: {
    id: string;
    name: string;
    url: string | null;
  };
  group: {
    admin_ids: string[];
    id: string;
    name: string;
  } | null;
  user: User;
  custom_fields: {
    id: string;
    field: {
      id: string;
      company_id: string;
      deleted_at: string | null;
      eligible_types: { type: string }[];
      perms_add_values: string[];
      perms_visibility: string[];
      name: string;
      type: string;
      is_all_scopes: boolean;
      is_required: boolean;
      scope: { entity_type: string; entity_id: string }[];
    };
    value: {
      id: string;
      is_default: boolean;
      value: string;
    };
  }[];
};

export type OneSubscription = Subscription & {
  cost_center?: {
    id: string;
    name: string;
  };
  budget_committed?: MonetaryValue<CurrenciesKey>;
};

export const subscriptionIsAboutToExpire = (subscription: Subscription) => {
  return (
    !subscription.is_renew_required &&
    subscription.months_before_card_expiry <= 2
  );
};

export const subscriptionCardNeedsReloading = (subscription: Subscription) => {
  return !!subscription.card.automatic_reload_fail;
};

export const canReloadSubscription = (
  subscription: Subscription,
  user: User,
  company: Company,
) => {
  const amountToReload = Math.max(
    0,
    subscription.budget_real_estimated_with_fees - subscription.card.available,
  );

  const currentUserCanReload =
    user.is_account_owner ||
    user.is_controller ||
    isGroupAdmin({
      user,
      company,
      is_teams_feature_enabled: hasTeamsFeatureEnabled(user, company),
      group_id: subscription.group_id ?? undefined,
    }) ||
    user.id === subscription.user_id;

  return Boolean(
    currentUserCanReload &&
      (company.balance_available_all_accounts === undefined ||
        company.balance_available_all_accounts > amountToReload) &&
      !!subscription.card.automatic_reload_fail,
  );
};

export const formatMoneyWithPrecision2 = (
  value: number,
  currency: Company['currency'],
  language: Language,
) => {
  return format(
    fromNumber(Number.parseFloat(value.toFixed(2)), currency, 2),
    language,
  );
};

export const extractSubscriptionSpentAmount = (subscription: Subscription) => {
  const availableBudgetAmount = Math.min(
    subscription.budget_declared ?? 0,
    subscription.card?.available_exchanged ?? 0,
  );
  const budgetAmount = subscription.budget_declared ?? 0;
  return Math.max(0, budgetAmount - availableBudgetAmount);
};

export const extractSubscriptionBudget = (
  subscription: Subscription,
  companyCurrency: Company['currency'],
  language: Language,
) => {
  const budgetAmount = subscription.budget_declared ?? 0;

  const spentAmountToBeUsed =
    companyCurrency === subscription.currency_declared
      ? subscription.outgoings_billed
      : subscription.outgoings_declared;
  const spentAmount = Math.min(spentAmountToBeUsed ?? 0, budgetAmount);

  const availablePercent =
    Math.round(
      (budgetAmount === 0
        ? 100
        : ((budgetAmount - spentAmount) * 100) / budgetAmount) * 10,
    ) / 10;

  if (companyCurrency === subscription.currency_declared) {
    return {
      companyCurrency: {
        budget: formatMoneyWithPrecision2(
          budgetAmount,
          subscription.currency_declared,
          language,
        ),
        availableCard: formatMoneyWithPrecision2(
          budgetAmount - spentAmount,
          subscription.currency_declared,
          language,
        ),
      },
      subscriptionCurrency: undefined,
      availablePercent,
    };
  }

  const budgetAmountConverted = subscription.budget_real_estimated ?? 0;
  const spentOnCardAmountConverted = subscription.outgoings_billed ?? 0;

  return {
    companyCurrency: {
      budget: formatMoneyWithPrecision2(
        budgetAmountConverted,
        companyCurrency,
        language,
      ),
      availableCard: formatMoneyWithPrecision2(
        budgetAmountConverted - spentOnCardAmountConverted,
        companyCurrency,
        language,
      ),
    },
    subscriptionCurrency: {
      budget: formatMoneyWithPrecision2(
        budgetAmount,
        subscription.currency_declared,
        language,
      ),
      availableCard: formatMoneyWithPrecision2(
        budgetAmount - spentAmount,
        subscription.currency_declared,
        language,
      ),
    },
    availablePercent,
  };
};

export const extractSubscriptionStatus = (subscription: Subscription) => {
  if (subscription.is_renew_required) {
    if (subscription.was_transferred) {
      return 'action_required';
    }
    return 'expired';
  }
  if (subscription.status === 'active') {
    return 'active';
  }
  return 'paused';
};

type SubscriptionStatus = ReturnType<typeof extractSubscriptionStatus>;

export const subscriptionStatusToLabel: Record<SubscriptionStatus, I18nKey> = {
  expired: 'subscriptions.status.expired',
  active: 'subscriptions.status.active',
  paused: 'subscriptions.status.paused',
  action_required: 'subscriptions.status.actionRequired',
};

export const subscriptionStatusToI18nKey = (subscription: Subscription) => {
  const status = extractSubscriptionStatus(subscription);
  return subscriptionStatusToLabel[status];
};

export const getIncreaseAmountFormatted = (
  requestAmount: number,
  subscriptionAmount: number,
  companyCurrency: Company['currency'],
  language: Language,
): string => {
  return formatMoneyWithPrecision2(
    requestAmount - subscriptionAmount,
    companyCurrency,
    language,
  );
};

export const filterSubscriptions = (
  subscriptions: Subscription[],
  searchTerm: string,
  t: TGlobalFunctionTyped,
): Subscription[] => {
  if (!searchTerm) {
    return subscriptions;
  }

  const fuse = new Fuse(
    subscriptions.map((sub) => ({
      ...sub,
      // Additional keys to help the search
      translatedStatus: t(
        subscriptionStatusToLabel[extractSubscriptionStatus(sub)],
      ),
      aboutToExpire: subscriptionIsAboutToExpire(sub)
        ? t('subscriptions.aboutToExpire')
        : '',
      writtenBudget: `${sub.budget_declared.toFixed(2)} ${sub.budget_declared
        .toFixed(2)
        .replace('.', ',')}`,
    })),
    {
      keys: [
        'budget_declared',
        'currency_declared',
        'custom_fields.value.value',
        'description',
        'group.name',
        'supplier.name',
        'supplier.url',
        'user.first_name',
        'user.last_name',
        'user.email',
        'user.fullname',
        'translatedStatus',
        'aboutToExpire',
        'writtenBudget',
      ],
      threshold: 0.1,
      location: 0,
      distance: 2900,
      useExtendedSearch: true,
      ignoreLocation: true,
      minMatchCharLength: 1,
    },
  );
  return fuse.search(searchTerm).map((result) => {
    // Remove the additional helper keys
    const { translatedStatus, aboutToExpire, writtenBudget, ...subscription } =
      result.item;
    return subscription;
  });
};

export const displaySupplierBillingUrl = (subscription: Subscription) => {
  const supplierUrl = prependUrlScheme(subscription.supplier_billing_url ?? '');
  return supplierUrl === '' ? '-' : supplierUrl;
};
